/**
 * Easy Pets — recently viewed products.
 *
 * On a product page, records the current product into localStorage (most-recent
 * first, deduped, capped). Renders the stored products (excluding the current
 * one) as compact cards. No network requests — all data is captured at view time.
 */
const EP_RV_KEY = 'easypets:recently-viewed';
const EP_RV_MAX_STORE = 12;

class EasypetsRecentlyViewed extends HTMLElement {
  connectedCallback() {
    /** @type {number} */
    this.limit = Number(this.dataset.limit) || 6;

    const current = this.#readCurrent();
    if (current) this.#record(current);

    this.#render(current?.handle);
  }

  /** @returns {{handle:string,title:string,url:string,image:string,price:string}|null} */
  #readCurrent() {
    const { currentHandle, currentTitle, currentUrl, currentImage, currentPrice } = this.dataset;
    if (!currentHandle || !currentUrl) return null;
    return {
      handle: currentHandle,
      title: currentTitle || '',
      url: currentUrl,
      image: currentImage || '',
      price: currentPrice || '',
    };
  }

  /** @returns {Array<object>} */
  #load() {
    try {
      const raw = localStorage.getItem(EP_RV_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  /** @param {object} product */
  #record(product) {
    let items = this.#load().filter((p) => p && p.handle !== product.handle);
    items.unshift(product);
    items = items.slice(0, EP_RV_MAX_STORE);
    try {
      localStorage.setItem(EP_RV_KEY, JSON.stringify(items));
    } catch {
      /* storage full or blocked — non-fatal */
    }
  }

  /** @param {string} [excludeHandle] */
  #render(excludeHandle) {
    const grid = this.querySelector('[data-rv-grid]');
    if (!grid) return;

    const items = this.#load()
      .filter((p) => p && p.handle !== excludeHandle)
      .slice(0, this.limit);

    if (items.length === 0) return; // stays hidden

    const html = items.map((p) => this.#cardHTML(p)).join('');
    grid.innerHTML = html;
    this.hidden = false;
  }

  /** @param {object} p @returns {string} */
  #cardHTML(p) {
    const img = p.image
      ? `<span class="easypets-rv__media"><img class="easypets-rv__img" src="${this.#attr(p.image)}" alt="${this.#attr(p.title)}" width="400" height="400" loading="lazy"></span>`
      : '';
    return `<li class="easypets-rv__cell"><a class="easypets-rv__card" href="${this.#attr(p.url)}">${img}<span class="easypets-rv__title">${this.#text(p.title)}</span><span class="easypets-rv__price">${this.#text(p.price)}</span></a></li>`;
  }

  /** Escape a value for use inside an HTML attribute. */
  #attr(value) {
    return String(value).replace(/"/g, '&quot;').replace(/</g, '&lt;');
  }

  /** Escape a value for use as text content. */
  #text(value) {
    const div = document.createElement('div');
    div.textContent = String(value);
    return div.innerHTML;
  }
}

if (!customElements.get('easypets-recently-viewed')) {
  customElements.define('easypets-recently-viewed', EasypetsRecentlyViewed);
}
